// This import *must* be the first import to ensure correct asset path resolution.
import 'public-path';
// This style import *must* stay as the first import of the file to maintain proper stylesheet ordering.
import 'styles/index.scss';
import '@classpass/visualizations/lib/visualizations.cjs.css';
import React, { Component, Suspense, useEffect, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { IntlProvider, injectIntl } from 'react-intl';
import 'intl-pluralrules'; // polyfill for IE11 & Safari 12-
import Modal from 'react-modal';
import _set from 'lodash/set';
import { intlShape } from 'constants/propTypes';
import createStoreWithParams from 'store/create';

import Banner from 'components/Banner';
import Decagon from 'components/Decagon';
import { Optimizely } from 'components/Optimizely';
import Snackbar from 'components/Snackbar';
import { UnwrappedRoute } from 'features/App';
import { setPike13ApiToken } from 'features/Migrate';
import { CookieBanner, CookieModal, CookiesContextProvider, YourPrivacyChoicesModal } from 'features/Privacy';
import { ServicesContextProvider } from 'features/ManageServices/Services';
import { formatUserInfo } from 'helpers/core';
import { installDomReconciliationGuards } from 'helpers/dom-reconciliation-guard';
import { httpClient } from 'helpers/http';
import namedFormats from 'helpers/intl-named-formats';
import { queryClient } from 'helpers/query-client';
import Segment from 'helpers/segment';

import { showDynamicRoutingWidget } from '@classpass/dynamic-routing-widget';
import LogRocket from 'logrocket';
import routes from './routes';

import { setGitSha } from './reducers/app';
import { setLocaleData } from './reducers/locale-data';
import { initializeLogRocketForOptimizely, initializeLogRocket } from './utils/logrocket';

const StoreContext = createContext(null);
const UserInfoContext = createContext(null);
const EnvContext = createContext(null);

// True constants that are safe at module level
const DEFAULT_LOCALE = 'en-US';

const rootEl = document.getElementById('root');
window.React = React;

if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement(rootEl);
}

// Component definitions that need to be before router creation

// Context-aware version of UnwrappedRoute that gets store and userInfo from context
function ContextAwareUnwrappedRoute(props) {
  const { store } = useContext(StoreContext);
  const { userInfo } = useContext(UserInfoContext);

  return <UnwrappedRoute store={store} userInfo={userInfo} {...props} />;
}

function NavigationProvider() {
  const { store } = useContext(StoreContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (store) {
      store.sagaDependencies.location = location;
      store.sagaDependencies.navigate = navigate;
    }
  }, [store, location, navigate]);

  return null;
}

function RootLayout() {
  const env = useContext(EnvContext);
  const { userInfo } = useContext(UserInfoContext);

  return (
    <>
      <NavigationProvider />
      <SegmentTracker />
      <Snackbar id="snackbar-notification" />
      <Decagon
        env={env?.ENV}
        language={env?.LANGUAGE}
        isAuthenticated={userInfo?.isAuthenticated}
        userEmail={userInfo?.email}
        venuesList={userInfo?.venues_list}
      />
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </>
  );
}

function SegmentTracker() {
  const location = useLocation();
  const { pathname, search } = location;

  useEffect(() => {
    Segment.makePageTracker(location);
  }, [pathname, search]);

  return null;
}

// Create router outside React tree (following React Router v6 best practices)
const createAppRouter = () => {
  return createBrowserRouter([
    {
      path: '/',
      element: <RootLayout />,
      children: routes.map(routeConfig => {
        const { path } = routeConfig;
        return {
          path: path === '/*' ? '*' : path,
          element: <ContextAwareUnwrappedRoute {...routeConfig} />,
        };
      }),
    },
  ]);
};

class StoreCreator extends Component {
  static propTypes = {
    language: PropTypes.string.isRequired,
    supplementalLocaleData: PropTypes.shape({ startWeekOn: PropTypes.string }),
    rawUserInfo: PropTypes.shape({}), // TODO
    children: PropTypes.func,
    intl: intlShape,
  };

  constructor(props) {
    super(props);
    const { rawUserInfo, intl } = props;
    const userInfo = formatUserInfo(rawUserInfo);
    const store = createStoreWithParams({
      sagaDependencies: {
        httpClient,
        intl,
        routes,
      },
      userInfo,
    });

    if (module.hot) {
      module.hot.accept();
    }

    window.store = store;
    this.state = { store, userInfo };
  }

  static getDerivedStateFromProps(props, state) {
    const { language, supplementalLocaleData } = props;
    const { store } = state;
    // https://github.com/axios/axios#custom-instance-defaults
    const updatedStore = _set(store, 'sagaDependencies.httpClient.defaults.headers.common.Accept-Language', language);
    store.dispatch(setLocaleData(language, supplementalLocaleData));
    store.dispatch(setPike13ApiToken(window.PIKE_13_TOKEN));
    store.dispatch(setGitSha(window.GIT_SHA));
    return updatedStore;
  }

  render() {
    const { children } = this.props;
    const { store, userInfo } = this.state;
    return children({ store, userInfo });
  }
}

const StoreCreatorWithIntl = injectIntl(StoreCreator);

function ThirdPartyManager({ userEmail }) {
  useEffect(() => {
    // Exclude Cypress from LogRocket session logging
    const isCypressUser = userEmail === 'cypress@classpass.com';
    if (!isCypressUser) {
      initializeLogRocket();
    }
    initializeLogRocketForOptimizely();
  }, []);

  return (
    <>
      <Optimizely />
    </>
  );
}

ThirdPartyManager.propTypes = {
  userEmail: PropTypes.string,
};

async function boot() {
  installDomReconciliationGuards();

  const ENV = window.ENV || '';
  const LOCALE = window.CP_LOCALE || DEFAULT_LOCALE;
  const LANGUAGE = window.CP_LANGUAGE || DEFAULT_LOCALE;
  const SUPPLEMENTAL_LOCALE_DATA = window.CP_SUPPLEMENTAL_LOCALE_DATA || {};
  const USER_INFO = window.CP_USER || {};

  // mirrored in /server/routes/reports.js
  // Load the locale messages JSON in every environment so that switching languages
  // updates translated UI (sidenav, buttons, etc.) in dev as well as production.
  const messages = await import(
    /* webpackChunkName: "[request]-locale" */
    `./locales/messages/${LANGUAGE}.json`
  ).then(module => module.default);

  // Send user info to LogRocket
  if (USER_INFO?.user_id) {
    const { first_name: firstName = '', last_name: lastName = '', email = '', user_id: userId } = USER_INFO;
    LogRocket.identify(`${userId}`, {
      name: `${firstName} ${lastName}`.trim(),
      email,
    });
  }

  const router = createAppRouter();

  function App() {
    return (
      <IntlProvider
        defaultLocale={DEFAULT_LOCALE}
        locale={LOCALE}
        messages={messages}
        defaultFormats={namedFormats}
        formats={namedFormats}
        textComponent="span">
        <StoreCreatorWithIntl
          language={LANGUAGE}
          supplementalLocaleData={SUPPLEMENTAL_LOCALE_DATA}
          rawUserInfo={USER_INFO}>
          {({ store, userInfo }) => (
            <Provider store={store}>
              <QueryClientProvider client={queryClient}>
                <ReactQueryDevtools initialIsOpen={false} />
                <StoreContext.Provider value={{ store }}>
                  <UserInfoContext.Provider value={{ userInfo }}>
                    <EnvContext.Provider value={{ ENV, LANGUAGE, LOCALE, SUPPLEMENTAL_LOCALE_DATA }}>
                      <ServicesContextProvider>
                        <CookiesContextProvider>
                          <CookieBanner />
                          <CookieModal />
                          <YourPrivacyChoicesModal />
                          <ThirdPartyManager userEmail={USER_INFO?.email} />
                          <Banner id="layout" />
                          <RouterProvider router={router} />
                        </CookiesContextProvider>
                      </ServicesContextProvider>
                    </EnvContext.Provider>
                  </UserInfoContext.Provider>
                </StoreContext.Provider>
              </QueryClientProvider>
            </Provider>
          )}
        </StoreCreatorWithIntl>
      </IntlProvider>
    );
  }

  if (window.SHOW_DYNAMIC_ROUTING_WIDGET) {
    showDynamicRoutingWidget();
  }

  render(<App />, rootEl);
}

const polyfills = [];

// Conditionally load timezone data for IE11
try {
  new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
} catch (error) {
  polyfills.push(
    import(
      /* webpackChunkName: "date-time-format-timezone" */
      'date-time-format-timezone'
    )
  );
}

if (!window.fetch) {
  polyfills.push(
    import(
      /* webpackChunkName: "whatwg-fetch" */
      'whatwg-fetch'
    )
  );
}

Promise.all(polyfills).then(boot);
